<template>
    <div>
        <!-- Formulario Horizontal Estándar -->
        <div class="card mb-4 shadow-sm">
            <div class="card-header font-weight-bold">Registrar Frecuencia Respiratoria</div>
            <div class="card-body">
                <form @submit.prevent="guardarRegistro" class="row align-items-end">
                    <div class="col-md-3 mb-2 mb-md-0">
                        <label class="small mb-1 font-weight-bold" for="inputValor">F. Respiratoria (RPM)</label>
                        <input class="form-control" id="inputValor" type="number" v-model="nuevoRegistro.respiracion" placeholder="Ej: 16" required>
                    </div>
                    <div class="col-md-3 mb-2 mb-md-0">
                        <label class="small mb-1 font-weight-bold" for="inputFecha">Fecha</label>
                        <input class="form-control" id="inputFecha" type="date" v-model="nuevoRegistro.fecha" required>
                    </div>
                    <div class="col-md-3 mb-2 mb-md-0">
                        <label class="small mb-1 font-weight-bold" for="inputHora">Hora</label>
                        <input class="form-control" id="inputHora" type="time" v-model="nuevoRegistro.hora" required>
                    </div>
                    <div class="col-md-3">
                        <button class="btn btn-primary btn-block shadow-sm" type="submit">
                            <i class="fas fa-plus mr-1"></i> Agregar Registro
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div class="row">
            <!-- Gráfica (Izquierda) -->
            <div class="col-xl-8 mb-4">
                <div class="card h-100 shadow-sm">
                    <div class="card-header font-weight-bold text-dark">
                        Gráfica de Frecuencia Respiratoria
                    </div>
                    <div class="card-body">
                        <div class="chart-area" style="height: 350px;">
                            <canvas ref="graficoRespiracion"></canvas>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Historial (Derecha) -->
            <div class="col-xl-4 mb-4">
                <div class="card h-100 shadow-sm">
                    <div class="card-header py-3 d-flex align-items-center justify-content-between">
                        <h6 class="m-0 font-weight-bold text-dark small text-uppercase">Historial de Respiración</h6>
                        <span class="badge badge-indigo-soft text-indigo">{{ data_chart.length }}</span>
                    </div>
                    <div class="card-body p-0" style="max-height: 400px; overflow-y: auto;">
                        <div class="list-group list-group-flush">
                            <div v-for="(item, index) in data_chart" :key="index" 
                                 class="list-group-item list-group-item-action py-2 px-3 border-bottom">
                                <div class="row align-items-center no-gutters">
                                    <div class="col-5">
                                        <div class="small font-weight-bold text-gray-700">{{ item.fecha | formatDate }} - {{ item.hora | formatTime }}</div>
                                        <div class="text-gray-600" v-if="item.user" style="font-size: 0.7rem;">
                                            <i class="fas fa-user-md mr-1 text-primary"></i>{{ item.user.name }}
                                        </div>
                                    </div>
                                    <div class="col-4 text-center border-left border-right">
                                        <span class="h5 mb-0 font-weight-bold text-indigo">{{ item.valor_uno }}</span>
                                        <span class="small text-muted ml-1">RPM</span>
                                    </div>
                                    <div class="col-3 text-right">
                                        <button class="btn btn-sm btn-icon btn-transparent-dark" 
                                                @click="eliminarRegistro(item.id)" v-if="item.can_delete">
                                            <i class="fas fa-trash-alt text-danger" style="font-size: 0.7rem;"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="data_chart.length === 0" class="p-4 text-center text-muted small italic">
                            No hay registros disponibles.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import dayjs from 'dayjs';
import formatosUrgenciaService from '@/services/formatosUrgenciaService.js';
import Toast from '../../../components/common/utilities/toast';
import Swal from 'sweetalert2';

export default {
    props: ['idUsuario', 'idHistoria'],
    data() {
        return {
            data_chart: [],
            nuevoRegistro: {
                respiracion: '',
                fecha: dayjs().format('YYYY-MM-DD'),
                hora: dayjs().format('HH:mm'),
            },
            chart: null
        }
    },
    filters: {
        formatDate(value) {
            return dayjs(value).format('DD/MM/YYYY');
        },
        formatTime(value) {
            return dayjs(`2000-01-01 ${value}`).format('hh:mm A');
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.inicializarGrafica();
            this.obtenerRegistros();
            this.nuevoRegistro.hora = dayjs().format('HH:mm');
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    },
    methods: {
        inicializarGrafica() {
            const canvas = this.$refs.graficoRespiracion;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            
            if (this.chart) {
                this.chart.destroy();
            }

            const sortedData = [...this.data_chart].reverse();
            const labels = sortedData.map(item => `${dayjs(item.fecha).format('DD/MM')} ${dayjs(`2000-01-01 ${item.hora}`).format('hh:mm A')}`);
            const values = sortedData.map(item => item.valor_uno);

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [{
                        label: 'RPM',
                        data: values,
                        borderColor: '#6610f2',
                        backgroundColor: 'rgba(102, 16, 244, 0.1)',
                        borderWidth: 3,
                        pointBackgroundColor: '#6610f2',
                        pointBorderColor: '#fff',
                        tension: 0.3,
                        fill: true
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        y: { beginAtZero: false },
                        x: { grid: { display: false } }
                    }
                }
            });
        },
        async obtenerRegistros() {
            try {
                this.LoaderSpinnerShow();
                const response = await formatosUrgenciaService.showSignosVitales({
                    id_historia: this.idHistoria,
                    tipo_signo: 'respiracion'
                });
                if (response.data.success) {
                    this.data_chart = response.data.data;
                    this.$nextTick(() => {
                        this.inicializarGrafica();
                    });
                }
            } catch (error) {
                console.error("Error al cargar registros:", error);
            } finally {
                this.LoaderSpinnerHide();
            }
        },
        async guardarRegistro() {
            try {
                this.LoaderSpinnerShow();
                const payload = {
                    id_historia: this.idHistoria,
                    tipo_signo: 'respiracion',
                    valor_uno: this.nuevoRegistro.respiracion,
                    fecha: this.nuevoRegistro.fecha,
                    hora: this.nuevoRegistro.hora,
                    unida:'RPM'
                };
                await formatosUrgenciaService.storeSignosVitales(payload);
                this.LoaderSpinnerHide();
                Toast.fire({ icon: 'success', title: 'Registro agregado correctamente' });
                this.nuevoRegistro.respiracion = '';
                this.nuevoRegistro.fecha = dayjs().format('YYYY-MM-DD');
                this.nuevoRegistro.hora = dayjs().format('HH:mm');
                this.obtenerRegistros();
            } catch (error) {
                Toast.fire({ icon: 'error', title: 'Error al guardar el registro' });
            } finally {
                this.LoaderSpinnerHide();
            }
        },
        async eliminarRegistro(id) {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: "No podrás revertir este cambio",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                try {
                    this.LoaderSpinnerShow();
                    const response = await formatosUrgenciaService.destroySignosVitales(id);
                    this.LoaderSpinnerHide();
                    if (response.data.success) {
                        Toast.fire({ icon: 'success', title: 'Registro eliminado' });
                        this.obtenerRegistros();
                    }
                } catch (error) {
                    console.error("Error al eliminar registro:", error);
                    Toast.fire({ icon: 'error', title: 'Error al eliminar el registro' });
                } finally {
                    this.LoaderSpinnerHide();
                }
            }
        }
    }
}
</script>

<style scoped>
.chart-area {
    position: relative;
    height: 300px;
    width: 100%;
}

</style>
